// Who is signed in to the course console.
//
// Course staff hold REAL accounts — email and password — unlike golfers in the phone app,
// who are anonymous. This is the paid side of the product, so the sign-in is the front door
// and everything else waits behind it.
//
// The one subtlety worth knowing: `getSession()` reads the token already in local storage
// and returns fast, but `onAuthStateChange` is what keeps us honest afterwards (refresh,
// sign-out in another tab, a token that expired while the laptop was asleep). We do both:
// getSession to stop the first paint from flashing a sign-in form at somebody who is already
// signed in, then the subscription for everything after that.

import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

export type SessionState = {
  /** The live session, or null when nobody is signed in. */
  session: Session | null;
  /** True until we have checked storage once. Render nothing decisive before this clears. */
  loading: boolean;
  /** Convenience: the signed-in address, for the header. */
  email: string | null;
};

export function useSession(): SessionState {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (!alive) return;
        setSession(data.session ?? null);
        setLoading(false);
      })
      .catch(() => {
        // A dead network on first load should still show the sign-in form rather than
        // spin forever.
        if (!alive) return;
        setLoading(false);
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!alive) return;
      setSession(next ?? null);
      setLoading(false);
    });

    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, loading, email: session?.user?.email ?? null };
}

/** Result of a sign-up attempt, in terms the form can actually say out loud. */
export type SignUpOutcome =
  | { kind: 'signed-in' }
  | { kind: 'check-email'; email: string }
  | { kind: 'already-registered' };

/**
 * Create a staff account.
 *
 * Email confirmation may be switched on for this project, in which case Supabase returns a
 * user but NO session — the person is not logged in yet and must click the link in their
 * inbox. Telling them "you're in!" at that point is the classic way to strand somebody on a
 * blank screen, so we report which of the two happened and let the form say the right thing.
 *
 * The third case is a quirk of Supabase's anti-enumeration behaviour: signing up with an
 * address that already exists succeeds, but hands back a user with an empty `identities`
 * array. That is the tell for "this email is taken" — and the right advice is "sign in".
 */
export async function signUp(email: string, password: string): Promise<SignUpOutcome> {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;

  if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
    return { kind: 'already-registered' };
  }
  if (data.session) return { kind: 'signed-in' };
  return { kind: 'check-email', email };
}

/** Sign in an existing staff account. Throws on bad credentials. */
export async function signIn(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
}

/** Sign out everywhere this browser is concerned. */
export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/** Send a password-reset email back to this console. */
export async function sendPasswordReset(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/console`,
  });
  if (error) throw error;
}

/** A stable sign-out callback for components that just need a button handler. */
export function useSignOut(): () => Promise<void> {
  return useCallback(() => signOut(), []);
}
